/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { ErrorBoundary } from 'react-error-boundary';
import { di } from 'react-magnetic-di';
import { shallowEqualObjects } from 'shallow-equal';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';
import { getRovoParams } from '@atlaskit/rovo-triggers/params';
import { type SmartLink3PPostAuthProvider } from '@atlaskit/rovo-triggers/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import {
	AutoSuggestSkills,
	type AutoSuggestSkillsRef,
} from '@atlassian/conversation-assistant-auto-suggest/auto-suggest-skills';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { ExperienceSuccess } from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	getRateLimitedActionType,
	isFullScreenError,
	isRateLimited,
	useChatSignals,
} from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import {
	useStagingAreaActions,
	useStagingAreaState,
} from '@atlassian/conversation-assistant-store/controllers/staging-area';
import {
	useRootUISelector,
	useUIStore,
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { useEditorPlaceholder } from '@atlassian/conversation-assistant-ui-components/controllers/useEditorPlaceholder';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import { ActionsProvider } from '@atlassian/rovo-conversation-actions/actions-context';
import { PROMPT_LIBRARY_SKILL_TAG } from '@atlassian/rovo-conversation-actions/prompt-cards';
import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';
import { isChatAction, isSkillAction } from '@atlassian/rovo-conversation-actions/utils';
import { ForYouNavigationProvider } from '@atlassian/rovo-for-you/navigation-store';
import { InsightsFeed } from '@atlassian/rovo-growth-pulse/ui/insights-feed';
import {
	ProfileTriggerProvider,
	type ProfileTriggerRenderer,
} from '@atlassian/rovo-platform-ui-components/profile-trigger';
import { useRovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal-provider';
import { RovoSkillsConfigProvider } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';
import { SkillMetadataProvider } from '@atlassian/rovo-skills/services/skill-metadata-provider';
import { useSpaceStoreActions } from '@atlassian/rovo-spaces/controllers/space-store';
import { type CreateSpaceResponse } from '@atlassian/rovo-spaces/controllers/space-store/types';
import { generateOptimisticSpaceSources } from '@atlassian/rovo-spaces/controllers/space-store/utils';
import { type CreateSpacePayload } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';
import { CreateSpaceModal } from '@atlassian/rovo-spaces/ui/CreateSpaceModal';
import { type SpaceSource } from '@atlassian/rovo-spaces/ui/CreateSpaceModal/types';
import { ManageSpaceModal } from '@atlassian/rovo-spaces/ui/ManageSpaceModal';
import { createEmojiProvider } from '@atlassian/rovo-spaces/utils/emoji-provider';
import { UserProfileCardWithTrigger } from '@atlassian/user-profile-card/trigger';

import { AutoConversationEvaluator } from './AutoConversationEvaluator';
import { SBSIntegrationWrapper } from './common/chatSideBySide/SBSIntegrationWrapper';
import {
	AI_MATE_EXPERIENCE_ID,
	MINI_MODAL_EXPANDED_HEIGHT,
	MINI_MODAL_GREETING_HEIGHT,
} from './common/constants/private';
import { CONVERSATION_ASSISTANT_MINIMUM_HEIGHT } from './common/constants/public';
import { BackBehaviorClearWhenConversationChanges } from './common/ui/BackBehaviorClearWhenConversationChanges';
import { useMiniModalAutoExpand } from './common/ui/hooks/use-mini-modal-auto-expand';
import { useMiniModalResize, type UseResizeReturn } from './common/ui/hooks/use-mini-modal-resize';
import { useMiniModalTabHeight } from './common/ui/hooks/use-mini-modal-tab-height';
import { InsightsChatExitedPublisher } from './common/ui/InsightsChatExitedPublisher';
import { getDictationOverlayBackground } from './common/utils/getDictationOverlayBackground';
import { useGlobalConfig } from './common/utils/global-config/useGlobalConfig';
import { PageTitle } from './common/utils/page-title';
import { useSideNav } from './common/utils/side-nav-context/useSideNav';
import { useUIConfig } from './common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from './common/utils/use-assistance-service-params';
import { useCalculatedScreenType } from './common/utils/use-calculated-screen-type';
import { useSelectedAgentAsPageTitle } from './common/utils/use-selected-agent-as-page-title';
import { useActiveAction } from './controllers/active-action';
import { getActionPresentation } from './controllers/active-action/getActionPresentation';
import { AnalyticsPropertiesUpdater } from './controllers/analytics';
import { isChatLoading } from './controllers/chat-store/isChatLoading';
import { isEmptyConversation } from './controllers/chat-store/isEmptyConversation';
import { useChatStore } from './controllers/chat-store/useChatStore';
import { useChatStoreActions } from './controllers/chat-store/useChatStoreActions';
import { useConversation } from './controllers/conversation/useConversation';
import { useSyncConversationStatusOnAgentError } from './controllers/conversation/useSyncConversationStatusOnAgentError';
import { FeedbackCollectorContainer } from './controllers/feedback-collector/FeedbackCollectorContainer';
import { ForgeAuthListener } from './controllers/forge-auth-listener';
import { useResendMessage } from './controllers/resend-message';
import { useSeedInsightConversation } from './controllers/seed-insight-conversation/useSeedInsightConversation';
import { useHydrateTaskStoreContext } from './controllers/tasks/use-hydrate-task-store-context';
import { createUnhandledErrorHandler } from './createUnhandledErrorHandler';
import { getChatGenericErrorType } from './getChatGenericErrorType';
import { useCombinedProviderAndCustomActions } from './hooks/use-combined-provider-and-custom-actions';
import { useEmptyChatLaunchLifecycle } from './hooks/use-empty-chat-launch-lifecycle';
import { useRovoSkillsProviderConfig } from './rovo-skills-provider-config-stabilization';
import {
	type ConversationAssistantConfig,
	type ConversationAssistantInternalProps,
	type ConversationAssistantModeConfig,
	type ConversationAssistantProps,
	ConversationAssistantUIModes,
} from './types';
import { type UIConfig } from './types';
import { ActiveTabProvider } from './ui/active-tab-provider/ActiveTabProvider';
import { AgentDebugModal } from './ui/agent-debug-modal';
import { AgentFavicon } from './ui/agent-favicon';
import { AgentModal } from './ui/agent-modal';
import { BrowseAgentsView } from './ui/browse-agents-view';
import { BrowseSpacesView } from './ui/browse-spaces-view';
import { ChatMainHeader as ChatMainHeaderRefresh } from './ui/chat-header-refresh';
import { ChatHistory } from './ui/chat-history';
import { FOR_YOU_TAB_ID } from './ui/chat-history/rovo-tabs/constants';
import { DataSculptorModal } from './ui/chat-history/store-assistant/data-sculptor-modal';
import { ChatInputOverlay } from './ui/chat-input-overlay/ChatInputOverlay';
import { ChatInputPlaceholderOverrideProvider } from './ui/chat-input-placeholder-override-context/ChatInputPlaceholderOverrideProvider';
import { ChatInputPlacementContainer } from './ui/chat-input-placement-container';
import { ChatInput as ChatInputRefresh } from './ui/chat-input-refresh/ChatInput';
import { DeactivatedAgentError } from './ui/chat-input/DeactivatedAgentError';
import { ChatOnboarding } from './ui/chat-onboarding';
import { InsightsConnectAppsAction } from './ui/connect-apps-button/InsightsConnectAppsAction';
import { ConversationList } from './ui/conversation-list';
import { ConversationList as ConversationListRefresh } from './ui/conversation-list-refresh';
import { DebugModal } from './ui/debug-modal';
import { FeedbackCollector } from './ui/feedback-collector';
import { MainFooter } from './ui/footer';
import { FullScreenModal } from './ui/full-screen-modal/FullScreenModal';
import { MainWrapper } from './ui/main-wrapper';
import { ActiveTabAwareChatHeader } from './ui/mini-modal-tab-components/ActiveTabAwareChatHeader';
import { ActiveTabAwareChatHistory } from './ui/mini-modal-tab-components/ActiveTabAwareChatHistory';
import { MiniModalTabAwareChatInput } from './ui/mini-modal-tab-components/MiniModalTabAwareChatInput';
import { PubSubListener } from './ui/pubsub-listener';
import {
	RecommendedSpacesPanel,
	type RecommendedSpacesPanelProps,
} from './ui/recommended-spaces-panel/RecommendedSpacesPanel';
import { RecommendedSpacesPanelLoading } from './ui/recommended-spaces-panel/RecommendedSpacesPanelLoading';
import { RovoSideNavigation } from './ui/rovo-side-navigation';
import { RovodalHost } from './ui/rovodal-host/RovodalHost';
import { SelectedAgentPublisher } from './ui/selected-agent-publisher';
import { SelectedSpaceBar } from './ui/selected-space-bar';
import { ShareConversationModalContainer } from './ui/share-conversation-modal/ShareConversationModalContainer';
import { ShareUpToMessageProvider } from './ui/share-conversation-modal/ShareUpToMessageProvider';
import {
	type ShareUpToMessage,
	type ShareUpToMessageContextValue,
} from './ui/share-conversation-modal/shareUpToMessageTypes';
import { useShareConversationModalState } from './ui/share-conversation-modal/useShareConversationModalState';
import { importSmartCreationWrapper } from './ui/smart-creation-wrapper/import-smart-creation-wrapper';
import { SpaceLandingPageContainer } from './ui/space-landing-page-container/SpaceLandingPageContainer';
import { StickySkillListener } from './ui/sticky-skill-listener/StickySkillListener';
import { StreamingStatusDispatcher } from './ui/streaming-status-dispatcher';

const styles = cssMap({
	mainContent: {
		height: '100%',
		width: '100%',
	},
	navBarWrapper: {
		height: '100%',
		width: '100%',
	},
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		overflow: 'auto',
	},
	contentFooter: {
		flexShrink: 0,
	},
	//Fullscreen styles
	contentWrapperCentered: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: '0px',
		height: '100%',
		width: '100%',
		overflow: 'hidden',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
	},
	contentWrapperCenteredPromptLibraryExpanded: {
		justifyContent: 'flex-start',
	},
	fullscreenPromptContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		alignSelf: 'stretch',
		width: '100%',
		minHeight: '0px',
	},
	fullscreenPromptContainerWithStagingAreaRef: {
		alignItems: 'stretch',
	},
	fullscreenPromptArea: {
		position: 'relative',
		alignSelf: 'stretch',
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		minHeight: '0px',
	},
	// MiniModal Styles
	miniModalMainContainer: {
		width: '400px',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.overlay'),
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		// Anchors the absolutely-positioned top resize handle to this container.
		position: 'relative',
	},
	miniModalHeaderContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
	},
	miniModalBodyContainer: {
		flexGrow: 1,
		minHeight: '0px',
		overflowY: 'auto',
		overflowX: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		scrollbarColor: `${token('color.border')} transparent`,
		scrollbarGutter: 'auto',
		scrollbarWidth: 'thin',
	},
	chatWithOverlayWrapper: {
		position: 'relative',
		flexGrow: 1,
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
	},
	miniModalFooterContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		position: 'sticky',
		bottom: 0,
	},
	miniModalMainActivePanelContainer: {
		width: '400px',
		overflowY: 'auto',
		overflowX: 'hidden',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.overlay'),
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		listStyleType: 'none',
		// Anchors the absolutely-positioned top resize handle to this container.
		position: 'relative',
	},
});

const LazySmartCreationWrapper = React.lazy(() =>
	importSmartCreationWrapper().then((module) => {
		return {
			default: module.SmartCreationWrapper,
		};
	}),
);

// Lazy-loaded conditionally-rendered pages. These are only loaded on demand
// when the user navigates to them via the sidebar menu.
const LazyPromptLibrary = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant_prompt-library" */ './ui/prompt-library/PromptLibrary'
	).then((m) => ({
		default: m.PromptLibrary,
	})),
);
const LazyScheduledChatListPage = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant_scheduled-chat-list" */ './ui/scheduled-chat-list/ScheduledChatListPage'
	).then((m) => ({ default: m.ScheduledChatListPage })),
);
const LazyTasksListPage = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant_tasks-list" */ './ui/tasks-list/tasks-list'
	).then((m) => ({
		default: m.TasksListPage,
	})),
);
const LazySearchWorkPage = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/rovo-pins_search-work" */ '@atlassian/rovo-pins/SearchWorkPage'
	).then((m) => ({ default: m.SearchWorkPage })),
);

const LazySmartLink3PPostAuthActions = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant_smartlink-3p-post-auth-actions" */ '@atlassian/conversation-assistant-onboarding/ui/SmartLink3PPostAuthActions'
	).then(({ SmartLink3PPostAuthActions }) => ({ default: SmartLink3PPostAuthActions })),
);

/**
 * Wrapper component that provides mini modal resize functionality.
 * Wraps the useMiniModalResize hook and passes results to renderMiniModalMainView.
 * Only rendered when mode === MINI_MODAL.
 *
 * @param renderMiniModalMainView - Render function that receives resize props
 * @param greetingHeight - Height of the greeting header (used to adjust min height calculation
 *                         so users can resize below the initial height when greeting is visible)
 * @param greetingKey - Unique identifier for the current greeting context (e.g., 'custom', 'default').
 *                      When this changes, resize state resets so the greeting can show with its default visibility.
 */
const MiniModalViewWrapper = ({
	renderMiniModalMainView,
	greetingHeight = 0,
	greetingKey,
	topOffset = 0,
	enableMiniModalResize,
}: {
	renderMiniModalMainView: (props: UseResizeReturn) => React.ReactNode;
	greetingHeight?: number;
	greetingKey?: string;
	topOffset?: number;
	enableMiniModalResize?: boolean;
}) => {
	const props = useMiniModalResize({
		minHeight: CONVERSATION_ASSISTANT_MINIMUM_HEIGHT,
		greetingHeight,
		greetingKey,
		topOffset,
		enableMiniModalResize,
		// No bottom clearance: the modal is anchored at the bottom and grows upward.
		bottomOffset: 0,
	});

	// Auto-expand the mini-modal when a chat begins (TREX-1422).
	useMiniModalAutoExpand({
		setSize: props.setSize,
		isResizeEnabled: props.isResizeEnabled,
	});

	return renderMiniModalMainView(props);
};

/**
 * New component for the new Rovo Chat Framework.
 * Will replace ConversationAssistantRender in the future.
 * Please use this component for AIFC chat refresh work.
 */
const FeedbackCollectorContainerWrapper = ({ children }: { children: React.ReactNode }) =>
	expValEquals('cc-mui-slides-experiment', 'isEnabled', true) ? (
		<FeedbackCollectorContainer>{children}</FeedbackCollectorContainer>
	) : (
		<>{children}</>
	);

/**
 * Mounts the per-message share context only when a share entry point exists.
 * When `shareUpToMessage` is undefined (feature gated off, or no active
 * conversation) the children render without the provider, so per-message
 * share buttons see no context and hide themselves — avoiding a dead
 * affordance without hoisting the whole render tree into a conditional.
 */
const ShareUpToMessageProviderWrapper = ({
	shareUpToMessage,
	isMessageShareable,
	shareDisabledReason,
	children,
}: {
	shareUpToMessage: ShareUpToMessage | undefined;
	isMessageShareable: (messageId: string) => boolean;
	shareDisabledReason: ShareUpToMessageContextValue['shareDisabledReason'];
	children: React.ReactNode;
}) =>
	shareUpToMessage ? (
		<ShareUpToMessageProvider
			shareUpToMessage={shareUpToMessage}
			isMessageShareable={isMessageShareable}
			shareDisabledReason={shareDisabledReason}
		>
			{children}
		</ShareUpToMessageProvider>
	) : (
		<>{children}</>
	);

type SpaceModalsWithStoreProps = {
	cloudId: string;
	isCreateSpaceModalOpen: boolean;
	onCloseCreateSpace: () => void;
	recommendedSpaceModal: { isOpen: boolean; initialName?: string; initialSources?: SpaceSource[] };
	onCloseRecommendedSpace: () => void;
	sideNavSpaceCreatedRef: React.MutableRefObject<
		| ((
				space: CreateSpaceResponse,
				sources: Array<{ ari: string; url: string; sourceType: string; name?: string }>,
				emojiId?: string,
		  ) => void)
		| null
	>;
	isManageSpaceModalOpen: boolean;
	onCloseManageSpace: () => void;
	selectedSpace: ReturnType<typeof useUIStore>['selectedSpace'];
	activeMenu: ReturnType<typeof useRootUISelector>[0]['activeMenu'];
	assistanceServiceParams: Parameters<typeof ManageSpaceModal>[0]['assistanceServiceParams'];
};

const SpaceModalsWithStore = ({
	cloudId,
	isCreateSpaceModalOpen,
	onCloseCreateSpace,
	recommendedSpaceModal,
	onCloseRecommendedSpace,
	sideNavSpaceCreatedRef,
	isManageSpaceModalOpen,
	onCloseManageSpace,
	selectedSpace,
	activeMenu,
	assistanceServiceParams,
}: SpaceModalsWithStoreProps): JSX.Element => {
	const { addSpace, patchSpace } = useSpaceStoreActions();
	// Keep the broad UI-store subscription while the experiment is off so this
	// action-only consumer re-renders on any UI-store change exactly as it did
	// when it read useUIStore() directly; when the experiment is on it subscribes
	// to nothing and the narrowed props drive re-renders instead.
	useUIStoreSubscription();
	const { setSelectedSpace, setActiveMenu } = useUIStoreActions();
	const { setSelectedConversation } = useChatStoreActions();

	const pendingSourcesRef = useRef<SpaceSource[]>([]);

	const handleCreate = useCallback((payload: CreateSpacePayload) => {
		pendingSourcesRef.current = payload.sources ?? [];
	}, []);

	const handleCreated = useCallback(
		(space: CreateSpaceResponse, emojiId?: string) => {
			addSpace(space, emojiId);
			const mappedSources = generateOptimisticSpaceSources(pendingSourcesRef.current);
			patchSpace(space.id, { sources: mappedSources });
			setSelectedSpace({
				id: space.id,
				name: space.name,
				emoji: emojiId ?? null,
				sources: mappedSources,
				entryPoint: recommendedSpaceModal.isOpen ? 'inChatRecommendation' : 'sidebar',
			});
			pendingSourcesRef.current = [];
			setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
			if (activeMenu !== 'none') setActiveMenu('none');
		},
		[
			addSpace,
			patchSpace,
			setSelectedSpace,
			setSelectedConversation,
			activeMenu,
			setActiveMenu,
			recommendedSpaceModal.isOpen,
		],
	);

	// Stable ref callback so RovoSideNavigation (outside provider) can trigger addSpace + patchSpace.
	sideNavSpaceCreatedRef.current = useCallback(
		(
			space: CreateSpaceResponse,
			sources: Array<{ ari: string; url: string; sourceType: string; name?: string }>,
			emojiId?: string,
		) => {
			addSpace(space, emojiId);
			patchSpace(space.id, { sources: generateOptimisticSpaceSources(sources) });
		},
		[addSpace, patchSpace],
	);

	return (
		<>
			<CreateSpaceModal
				isOpen={isCreateSpaceModalOpen}
				onClose={onCloseCreateSpace}
				onCreate={handleCreate}
				onCreated={handleCreated}
				cloudId={cloudId}
			/>
			<CreateSpaceModal
				isOpen={recommendedSpaceModal.isOpen}
				onClose={onCloseRecommendedSpace}
				onCreate={handleCreate}
				onCreated={handleCreated}
				cloudId={cloudId}
				initialName={recommendedSpaceModal.initialName}
				initialSources={recommendedSpaceModal.initialSources}
			/>
			{selectedSpace && (
				<ManageSpaceModal
					isOpen={isManageSpaceModalOpen}
					onClose={onCloseManageSpace}
					spaceId={selectedSpace.id}
					cloudId={cloudId}
					initialName={selectedSpace.name}
					initialEmoji={selectedSpace.emoji}
					assistanceServiceParams={assistanceServiceParams}
					onSuccess={(updated, sources) => {
						const m = generateOptimisticSpaceSources(sources);
						setSelectedSpace({
							id: updated.id,
							name: updated.name,
							emoji: updated.emoji,
							sources: m,
							entryPoint: selectedSpace.entryPoint,
						});
						patchSpace(updated.id, { name: updated.name, emoji: updated.emoji, sources: m });
					}}
				/>
			)}
		</>
	);
};

const ConversationAssistantRenderComponent = ({
	testId,
	isNav4Enabled,
	sitePickerRender,
	modeConfig,
	getActions,
	prioritizeCustomActionResults,
	numStartersToShow,
	topOffset = 0,
	isInPreviewModal,
	config,
	setSharedFeaturesInstrumentationId,
	tags,
	suppressInitialFocus,
	showActionsButton = true,
	activeMenu,
	isChatOnboardingMiniModalOpen,
	selectedSpace,
	smartLink3PPostAuth,
}: ConversationAssistantInternalProps & {
	modeConfig?: ConversationAssistantModeConfig;
	isNav4Enabled?: boolean;
	getActions?: () => Promise<ConversationAction[]>;
	prioritizeCustomActionResults?: boolean;
	numStartersToShow?: number;
	topOffset?: number;
	isInPreviewModal?: boolean;
	config: ConversationAssistantConfig;
	setSharedFeaturesInstrumentationId: (sharedFeaturesInstrumentationId: string | undefined) => void;
	tags?: ConversationAssistantProps['tags'];
	suppressInitialFocus?: ConversationAssistantProps['suppressInitialFocus'];
	showActionsButton?: boolean;
	activeMenu: ReturnType<typeof useRootUISelector>[0]['activeMenu'];
	isChatOnboardingMiniModalOpen: boolean;
	selectedSpace: ReturnType<typeof useRootUISelector>[0]['selectedSpace'];
	smartLink3PPostAuth: ReturnType<typeof useRootUISelector>[0]['smartLink3PPostAuth'];
}): JSX.Element => {
	di(
		useChatStore,
		useAnalytics,
		ChatHistory,
		ChatGenericError,
		useUIStore,
		useUIConfig,
		ConversationList,
		AgentModal,
		FeedbackCollector,
		useUserPreferences,
		fg,
		useConversation,
		useChatSignals,
		useGlobalConfig,
		ChatMainHeaderRefresh,
	);
	const { sendAnalyticsEvent } = useAnalytics();
	const isSmartLink3PPostAuthEnabled = fg('platform_sl_3p_post_auth_chat_open_fg');
	const [editorPopupsMountPoint, setEditorPopupsMountPoint] = useState<HTMLDivElement | null>(null);

	const handleUnhandledError = createUnhandledErrorHandler(
		sendAnalyticsEvent,
		'ConversationAssistantRender',
	);

	const { conversation, selectedAgent, agentErrorCode, agentIdForNewChat } = useConversation();

	// Undefined for the default Rovo agent — used to suppress tabs and reset tab state.
	const selectedAgentId = selectedAgent?.is_default ? undefined : selectedAgent?.id;
	const { abortRequests, setSelectedConversation, fetchConversationChannelsData } =
		useChatStoreActions();
	const [{ currentConversationId }] = useChatStore();
	const rovoSkillsConfig = useRovoSkillsProviderConfig({ tags });

	if (fg('convo-ai-enable-agents-use-permission')) {
		// Sync agent error state to conversation status (AGENT_FORBIDDEN or AGENT_DEACTIVATED)
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useSyncConversationStatusOnAgentError({
			agentErrorCode,
			conversationId: currentConversationId,
		});
	}
	const { setActiveMenu, setMounted } = useUIStoreActions();

	// Prefer the emoji provider passed in via config (e.g. from townsquare's cached
	// getEmojiProviderForCloudId) to avoid duplicate network requests. Fall back to
	// creating one locally only if no provider was supplied.
	const spaceEmojiProvider = useMemo(
		() => config.emojiProvider ?? (config.siteId ? createEmojiProvider(config.siteId) : undefined),
		[config.emojiProvider, config.siteId],
	);

	const insightsFeedAssistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'rovo-insights-feed',
	});
	// Stable-by-content assistanceServiceParams for the memoized InsightsFeed.
	const insightsFeedAssistanceServiceParamsKey = JSON.stringify(
		insightsFeedAssistanceServiceParams,
	);
	const stableInsightsFeedAssistanceServiceParams = useMemo(
		() => JSON.parse(insightsFeedAssistanceServiceParamsKey),
		[insightsFeedAssistanceServiceParamsKey],
	);

	useHydrateTaskStoreContext();

	const {
		fullScreen,
		externalEmbed,
		elements,
		customHandlers,
		customGreeting,
		hideBrowserTabTitle,
		hideBrowserTabFavicon,
		stagingAreaRef,
		disableSettingsModal,
		enableSkills,
		enableAgentMentions,
		enableAutoSuggest,
		enableMiniModalResize,
		initialPromptText,
		shouldSeedInitialPromptText,
	} = useUIConfig();
	const [{ conversationError, status: chatStatus }] = useChatSignals();
	const isFullScreenErrorState = isFullScreenError(conversationError);
	const isRateLimitedState = isRateLimited(conversationError);
	const rateLimitedActionType = getRateLimitedActionType(conversationError);
	const { insertPlaceholder } = useEditorPlaceholder();
	const {
		preventAbortRequestsOnStagingModalClose,
		preventAbortRequestsOnStandaloneChatClose,
		profileData,
		closeChat,
	} = useGlobalConfig();
	const chatLoading = isChatLoading(chatStatus, conversation?.status);
	const isEmptyConvo = isEmptyConversation(currentConversationId);

	const { activeAction, clearActiveAction } = useActiveAction();
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
	const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
		'rovo_growth_chat_pulse',
		'isEnabled',
		false,
	);

	// Profile-trigger renderer injected into the ai-mate render cluster (rovo-growth-pulse /
	// rovo-platform-ui-components). Those packages must NOT import @atlassian/user-profile-card
	// directly (doing so forms a package dependency cycle), so this renderer is supplied here —
	// conversation-assistant sits above that cluster and safely depends on user-profile-card.
	// Mounted high enough to wrap BOTH the InsightsFeed and the message content-renderer surfaces.
	// Gated on the migration experiment; when disabled, consumers fall back to legacy profile cards.
	const viewerId = profileData?.aaid;
	const renderProfileTrigger = useMemo<ProfileTriggerRenderer | undefined>(() => {
		if (FeatureGates.getExperimentValue('pt_user_profile_card_migration_exp', 'isEnabled', false)) {
			return ({
				userId,
				cloudId,
				children,
			}: {
				userId: string;
				cloudId: string;
				children: React.ReactNode;
			}) => (
				<UserProfileCardWithTrigger
					userId={userId}
					cloudId={cloudId}
					orgId={config.orgId ?? ''}
					viewerId={viewerId ?? ''}
					trigger="hover"
				>
					{children}
				</UserProfileCardWithTrigger>
			);
		}
		return undefined;
	}, [config.orgId, viewerId]);

	const activeActionPresentation = getActionPresentation(activeAction);
	const shouldSuppressPromptLibrary = Boolean(activeActionPresentation.suppressPromptLibrary);
	const shouldRenderPromptLibrary =
		fullScreen && isEmptyConvo && !chatLoading && !shouldSuppressPromptLibrary;
	const { isSideNavOpen, setSideNavOpen } = useSideNav();
	const menuButtonRef = useRef<HTMLButtonElement | null>(null);

	const [autoSuggestSearchQuery, setAutoSuggestSearchQuery] = useState('');
	const [isAutoSuggestSkillsVisible, setIsAutoSuggestSkillsVisible] = useState(false);
	const autoSuggestSkillsControllerRef = useRef<AutoSuggestSkillsRef | null>(null);
	const onAutoSuggestSkillsDismissHandle = useCallback((handle: AutoSuggestSkillsRef | null) => {
		autoSuggestSkillsControllerRef.current = handle;
	}, []);
	const [showInput, setShowInput] = useState(true);
	const [isPromptLibraryExpanded, setIsPromptLibraryExpanded] = useState(false);

	const smartLinkPostAuthProps =
		smartLink3PPostAuth?.isActive &&
		isSmartLink3PPostAuthEnabled &&
		smartLink3PPostAuth.extensionKey &&
		smartLink3PPostAuth.provider &&
		smartLink3PPostAuth.projectContext
			? {
					extensionKey: smartLink3PPostAuth.extensionKey,
					provider: smartLink3PPostAuth.provider as SmartLink3PPostAuthProvider,
					projectContext: smartLink3PPostAuth.projectContext,
				}
			: undefined;

	const { open: openRovoSettingsModal } = useRovoSettingsModal();
	const onOpenMemoryManagement = useCallback(
		() => openRovoSettingsModal({ defaultTab: 'manage-memory' }),
		[openRovoSettingsModal],
	);
	// Listen for custom event from post-office memory settings spotlight to open memory management modal
	useEffect(
		() =>
			bind(window, {
				type: 'memory-settings-onboarding-spotlight-primary-action-click',
				listener: () => onOpenMemoryManagement(),
			}),

		[onOpenMemoryManagement],
	);

	const handlePromptLibraryExpandedChange = useCallback((expanded: boolean) => {
		setIsPromptLibraryExpanded(expanded);
	}, []);
	const { resetStagingArea } = useStagingAreaActions();
	const { stagingAreaMode, stagingAreaEntryPoint } = useStagingAreaState();

	const isAifcOnboardingConfluence = stagingAreaEntryPoint === 'aifc-onboarding-confluence';

	const { screenType, openInlineBrowse, closeInlineBrowse } =
		useCalculatedScreenType(currentConversationId);

	const {
		mode,
		switchToMode,
		isMiniModalAvailable,
		isSidebarAvailable = true,
		isFullScreenModalAvailable,
	} = modeConfig ?? {
		mode: ConversationAssistantUIModes.SIDEBAR,
		isMiniModalAvailable: undefined,
		switchToMode: undefined,
		isSidebarAvailable: true,
		isFullScreenModalAvailable: undefined,
	};

	useEmptyChatLaunchLifecycle(currentConversationId);

	const {
		mergedGetActions,
		replaceProviderActions,
		numStartersToShow: customNumStartersToShow,
	} = useCombinedProviderAndCustomActions(getActions);

	useEffect(() => {
		if (elements?.stagingAreaEnabled) {
			setActiveMenu('none');
		}
	}, [elements?.stagingAreaEnabled, setActiveMenu]);

	useEffect(() => {
		if (
			!elements?.stagingAreaEnabled &&
			stagingAreaMode === 'inline' &&
			fg('aifc_create_enabled')
		) {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			setSharedFeaturesInstrumentationId(uuidv4());
		} else {
			setSharedFeaturesInstrumentationId(undefined);
		}
	}, [elements?.stagingAreaEnabled, stagingAreaMode, setSharedFeaturesInstrumentationId]);

	// Open prompt library when rovoPromptLibraryOpen param is present in the URL
	const hasOpenedPromptLibraryFromParamsRef = useRef(false);
	useEffect(() => {
		if (!shouldRenderPromptLibrary || hasOpenedPromptLibraryFromParamsRef.current) {
			return;
		}
		const rovoParams = getRovoParams();
		if (rovoParams.promptLibraryOpen) {
			setIsPromptLibraryExpanded(true);
			hasOpenedPromptLibraryFromParamsRef.current = true;
		}
	}, [shouldRenderPromptLibrary]);

	// Open pulse menu when rovoChatPathway=pulse param is present in the URL
	const hasOpenedPulseFromParamsRef = useRef(false);
	useEffect(() => {
		if (hasOpenedPulseFromParamsRef.current) {
			return;
		}
		const rovoParams = getRovoParams();
		if (rovoParams.pathway === 'pulse') {
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
			const isPulseExperimentEnabled = FeatureGates.getExperimentValue(
				'rovo_growth_chat_pulse',
				'isEnabled',
				false,
			);
			if (isPulseExperimentEnabled) {
				setActiveMenu('pulse');
				hasOpenedPulseFromParamsRef.current = true;
			}
		}
	}, [setActiveMenu]);

	const handleNewChatClick = useCallback(() => {
		setSelectedConversation({
			conversationId: EMPTY_CONVERSATION_ID,
			sourceAgentId: fg('convo-ai-enable-agents-use-permission')
				? agentIdForNewChat
				: selectedAgent?.id,
		});
		if (!fullScreen) {
			setActiveMenu('none');
		}
		clearActiveAction();
	}, [
		setSelectedConversation,
		fullScreen,
		setActiveMenu,
		clearActiveAction,
		agentIdForNewChat,
		selectedAgent?.id,
	]);

	const handleDiscoverSkillsClick = useCallback(() => {
		openInlineBrowse('skills');
	}, [openInlineBrowse]);

	const handleDiscoverPromptLibraryClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'libraryOpenedFromSidebar',
			attributes: {
				source: 'discoverActionButton',
			},
		});
		openInlineBrowse('prompt-library');
	}, [openInlineBrowse, sendAnalyticsEvent]);

	const handleBrowsePromptLibraryFromMenuClick = useCallback(() => {
		openInlineBrowse('prompt-library');
	}, [openInlineBrowse]);

	const [isCreateSpaceModalOpen, setIsCreateSpaceModalOpen] = useState(false);
	const [isManageSpaceModalOpen, setIsManageSpaceModalOpen] = useState(false);
	const [recommendedSpaceModal, setRecommendedSpaceModal] = useState<{
		isOpen: boolean;
		initialName?: string;
		initialSources?: SpaceSource[];
	}>({ isOpen: false });
	const handleCreateRecommendedSpace = useCallback<RecommendedSpacesPanelProps['onCreateSpace']>(
		(title, sources) => {
			setRecommendedSpaceModal({
				isOpen: true,
				initialName: title,
				initialSources: sources,
			});
		},
		[],
	);
	const handleCreateSpaceClick = useCallback(() => {
		setIsCreateSpaceModalOpen(true);
	}, []);

	const handleAutoSuggestQueryChange = useCallback((query: string) => {
		setAutoSuggestSearchQuery(query);
		if (query.trim() === '') {
			setIsAutoSuggestSkillsVisible(false);
		}
	}, []);

	const handleViewAllSkillsFromAutoSuggest = useCallback(() => {
		autoSuggestSkillsControllerRef.current?.dismiss();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoChatDiscoverSkillsButton',
		});
		openInlineBrowse('skills');
	}, [openInlineBrowse, sendAnalyticsEvent]);

	useEffect(() => {
		if (screenType === 'loading' && fullScreen) {
			setShowInput(false);
			return;
		}

		if (!activeAction) {
			setShowInput(true);
			return;
		}

		setShowInput('customFollowUpContext' in activeAction);
	}, [activeAction, screenType, fullScreen]);

	// Refetch the conversations by resetting the search value
	useEffect(() => {
		const focusWindowHandler = bind(window, {
			// We use visibilitychange instead of focus to prevent triggering refetch when moving between iframes such as agent embed
			type: 'visibilitychange',
			listener: () => {
				if (getDocument()?.visibilityState === 'visible') {
					fetchConversationChannelsData({
						reconcileFirstPage: true,
						onError: () => {},
					});
				}
			},
		});

		return () => {
			focusWindowHandler();
		};
	}, [fetchConversationChannelsData]);

	// Useful for integrators to know when Rovo is mounted and ready
	useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, [setMounted]);

	// Use stable refs to keep track of updates after mount
	const changingMode = useRef(false);
	// Declared in the parent (not in AutoConversationEvaluator) so the guard persists across
	// unmount/remount cycles of the child — which happen when isEmptyConvo toggles during navigation.
	// A ref local to the child would reset on every remount, defeating the guard.
	const autoConversationFiredRef = useRef(false);
	const preventAbortRequestsOnStagingModalCloseRef = useRef(
		preventAbortRequestsOnStagingModalClose,
	);
	const preventAbortRequestsOnStandaloneChatCloseRef = useRef(
		preventAbortRequestsOnStandaloneChatClose,
	);
	const isInPreviewModalRef = useRef(isInPreviewModal);

	useEffect(() => {
		preventAbortRequestsOnStagingModalCloseRef.current = preventAbortRequestsOnStagingModalClose;
	}, [preventAbortRequestsOnStagingModalClose]);

	useEffect(() => {
		preventAbortRequestsOnStandaloneChatCloseRef.current =
			preventAbortRequestsOnStandaloneChatClose;
	}, [preventAbortRequestsOnStandaloneChatClose]);

	useEffect(() => {
		isInPreviewModalRef.current = isInPreviewModal;
	}, [isInPreviewModal]);

	// AI MAU - When to fire dismissed event when component unmounts
	useEffect(
		() => {
			return () => {
				// do not abort requests when switching modes
				if (changingMode.current) {
					// just a safety if we are no unmounting the component
					// for whatever reason on changing mode, then on next natural
					// unmount we still want to abort requests
					changingMode.current = false;
					return;
				}

				// Do not abort requests when closing the staging modal (if requested by caller)
				if (preventAbortRequestsOnStagingModalCloseRef.current && isInPreviewModalRef.current) {
					return;
				}

				// Do not abort requests when closing standalone chat (if requested by caller)
				if (preventAbortRequestsOnStandaloneChatCloseRef.current && !isInPreviewModalRef.current) {
					return;
				}

				//TODO FIX THIS
				abortRequests({ deepResearchEnabled: undefined });
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const switchToModeCallbackInterceptor = useCallback(
		(mode: ConversationAssistantUIModes) => {
			if (switchToMode) {
				changingMode.current = true;
				switchToMode(mode);
			}
		},
		[switchToMode],
	);

	// Switching UI is enabled when at least two modes are available.
	const canSwitch =
		isFullScreenModalAvailable && fg('agent_session_experience_fg')
			? [isSidebarAvailable, isMiniModalAvailable, isFullScreenModalAvailable].filter(Boolean)
					.length >= 2
			: Boolean(isMiniModalAvailable && isSidebarAvailable);

	const isInlineBrowseScreen = screenType === 'skills' || screenType === 'prompt-library';

	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
	const isMemoryManagementEnabled = FeatureGates.getExperimentValue(
		'memory_management_ux',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

	// Settings menu item is shown when any settings-related feature is enabled
	// and the settings modal is not disabled (e.g. Chrome extension sidebar)
	const isSettingsEnabled = isMemoryManagementEnabled && !disableSettingsModal;

	const onSettingsClick = isSettingsEnabled ? onOpenMemoryManagement : undefined;
	const onChatHeaderMenuButtonClick = useCallback(() => {
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'rovoSideNavMenuButton',
		});
		setSideNavOpen(true);
	}, [sendAnalyticsEvent, setSideNavOpen]);

	// Local state for the share-snapshot modal that opens from the chat
	// header's more menu. The container is always mounted; the modal
	// itself only renders when `state.open()` is called.
	const shareConversationModalState = useShareConversationModalState({
		conversationId: currentConversationId,
		orgId: config.orgId,
		siteId: config.siteId,
		conversationStatus: conversation?.status,
		currentUserId: config.userId,
		// Optional override; when unset the share link defaults to the Home host
		// selected from the current origin.
		shareBaseUrl: config.shareBaseUrl,
	});
	const onSideNavClose = useCallback(() => {
		setSideNavOpen(false);
		requestAnimationFrame(() => {
			menuButtonRef.current?.focus();
		});
	}, [setSideNavOpen]);

	// A host-supplied `onShareClick` always wins — embedders may have
	// their own share UX or their own definition of "shareable", so we
	// never override or gate their handler. When the host does not
	// supply one we fall back to opening the local share-snapshot modal,
	// but only when there is an active conversation to share. Resolving
	// to `undefined` causes `ChatHeaderMoreMenu` to hide the share menu
	// entry entirely (see its `typeof onShareClick === 'function'` check).
	const onShareClick = useMemo<(() => void) | undefined>(() => {
		if (customHandlers?.onShareClick) {
			return customHandlers.onShareClick;
		}
		if (currentConversationId && currentConversationId !== EMPTY_CONVERSATION_ID) {
			return shareConversationModalState.open;
		}
		return undefined;
	}, [customHandlers?.onShareClick, currentConversationId, shareConversationModalState.open]);

	// Per-message "share chat up to this point" reuses the exact resolution as the header
	// `onShareClick` above (host handler → active-conversation → open, all of
	// which already accept an optional message id) — it is simply that same
	// callback controlled by the conversation-sharing experiment. Deriving it here rather
	// than re-implementing the branching keeps the two in lock-step.
	//
	// Per-message sharing builds on the wider conversation-sharing feature and
	// must never appear without it. When the experiment is disabled this resolves
	// to `undefined`, so the provider is not mounted and the buttons stay hidden.
	//
	// The experiment is evaluated inside the memo so its exposure fires only
	// when the memo re-computes, not on every render.
	const shareUpToMessage = useMemo<((messageId: string) => void) | undefined>(() => {
		const isPerMessageShareEnabled = FeatureGates.getExperimentValue(
			'rovo_conversation_sharing',
			'isEnabled',
			false,
		);
		return isPerMessageShareEnabled ? onShareClick : undefined;
	}, [onShareClick]);

	const chatHeaderProps = useMemo(
		() => ({
			mode,
			switchToMode: canSwitch ? switchToModeCallbackInterceptor : undefined,
			onMenuButtonClick: onChatHeaderMenuButtonClick,
			menuButtonRef,
			screenType: screenType,
			previousView: isInlineBrowseScreen ? closeInlineBrowse : undefined,
			sitePickerRender: sitePickerRender,
			onSettingsClick,
			onBrowseAgentClick: customHandlers?.onBrowseAgentClick,
			onShareClick,
			isShareDisabled: shareConversationModalState.shareDisabledReason,
			isFullScreenModalAvailable,
		}),
		[
			mode,
			canSwitch,
			switchToModeCallbackInterceptor,
			onChatHeaderMenuButtonClick,
			menuButtonRef,
			screenType,
			isInlineBrowseScreen,
			closeInlineBrowse,
			sitePickerRender,
			onSettingsClick,
			customHandlers?.onBrowseAgentClick,
			onShareClick,
			shareConversationModalState.shareDisabledReason,
			isFullScreenModalAvailable,
		],
	);

	const chatRefreshHeader = useMemo(
		() => <ChatMainHeaderRefresh {...chatHeaderProps} />,
		[chatHeaderProps],
	);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'aiMatePanel',
			mode: fullScreen ? 'fullscreen' : mode,
			attributes: {
				...(suppressInitialFocus && { wasAutoOpened: true }),
			},
		});
		// Should only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onSideNavSpaceCreatedRef = useRef<
		| ((
				space: CreateSpaceResponse,
				sources: Array<{ ari: string; url: string; sourceType: string; name?: string }>,
				emojiId?: string,
		  ) => void)
		| null
	>(null);

	const navigationRefreshOrNull = elements?.navbar ? (
		<RovoSideNavigation
			isOpen={isSideNavOpen}
			onClose={onSideNavClose}
			chatMode={mode}
			onSpaceCreated={(space, sources, emojiId) =>
				onSideNavSpaceCreatedRef.current?.(space, sources, emojiId)
			}
		/>
	) : null;

	// render rovo-navigation regardless of which menu we are on, but do not render it if navbar is disabled
	const renderNavAndActivePanelMenu = () => {
		let menu: React.ReactNode | null = null;
		const header = chatRefreshHeader;
		if (activeMenu === 'conversation-list') {
			if (fg('rovo_chat_chat_history_refresh')) {
				menu = <ConversationListRefresh chatRefreshHeader={header} />;
			} else {
				menu = <ConversationList chatRefreshHeader={header} />;
			}
		} else if (activeMenu === 'tasks') {
			menu = (
				<>
					{fullScreen && externalEmbed ? header : null}
					<React.Suspense fallback={null}>
						<LazyTasksListPage chatRefreshHeader={header} />
					</React.Suspense>
				</>
			);
		} else if (activeMenu === 'agents') {
			menu = <BrowseAgentsView chatRefreshHeader={header} />;
		} else if (
			activeMenu === 'browse-spaces' &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
		) {
			menu = <BrowseSpacesView chatRefreshHeader={header} />;
		} else if (activeMenu === 'scheduled') {
			menu = (
				<React.Suspense fallback={null}>
					<LazyScheduledChatListPage chatRefreshHeader={header} />
				</React.Suspense>
			);
		} else if (
			activeMenu === 'search-work' &&
			FeatureGates.getExperimentValue('rovo_your_work_tab_pin_objects', 'isEnabled', false, {
				fireExperimentExposure: false,
			})
		) {
			menu = (
				<React.Suspense fallback={null}>
					<LazySearchWorkPage
						cloudId={config.siteId}
						product={config.product}
						fullScreen={fullScreen}
						chatRefreshHeader={header}
					/>
				</React.Suspense>
			);
		} else if (
			activeMenu === 'recommended-spaces' &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
		) {
			menu = (
				<Suspense
					fallback={
						<RecommendedSpacesPanelLoading
							onCreateSpace={handleCreateRecommendedSpace}
							chatRefreshHeader={header}
						/>
					}
					interactionName="recommended-spaces-panel"
				>
					<RecommendedSpacesPanel
						cloudId={config.siteId}
						userId={profileData?.aaid ?? undefined}
						onCreateSpace={handleCreateRecommendedSpace}
						chatRefreshHeader={header}
					/>
				</Suspense>
			);
		} else if (
			activeMenu === 'space-landing' &&
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
		) {
			menu = (
				<Suspense fallback={null} interactionName="space-landing-page">
					<SpaceLandingPageContainer chatRefreshHeader={header} />
				</Suspense>
			);
		} else if (activeMenu === 'pulse') {
			menu = (
				<InsightsFeed
					chatRefreshHeader={header}
					conversationId={currentConversationId}
					assistanceServiceParams={stableInsightsFeedAssistanceServiceParams}
					onInsightReady={onInsightReady}
					isFullScreen={fullScreen}
					connectAppsAction={({ onVisibilityChange }) => (
						<InsightsConnectAppsAction
							isCompact={!fullScreen}
							onVisibilityChange={onVisibilityChange}
						/>
					)}
				/>
			);
		}

		return (
			<>
				{/* navigationRefresh is null if navbar is disabled */}
				{navigationRefreshOrNull}
				{menu ? (
					<>
						{/* for UFO performance tracking dimensions */}
						<UFOCustomData data={{ activeMenu: activeMenu }} />
						{menu}
					</>
				) : undefined}
			</>
		);
	};

	const handleClickStarter = useCallback(
		(starter: ConversationAction) => {
			if (fullScreen) {
				insertPlaceholder(starter.title);
				return;
			}

			if ('dynamicPrompt' in starter) {
				const shouldBeSkillAction =
					isSkillAction(starter) || (isChatAction(starter) && starter.encodeDynamicPromptAsSkill);
				insertPlaceholder(
					starter.dynamicPrompt ?? starter.title,
					shouldBeSkillAction ? 'skill' : undefined,
				);
				return;
			}
		},
		[insertPlaceholder, fullScreen],
	);

	// Ref shared between ActiveTabProvider and the mini-modal outer container so that
	// captureShellHeight can read the real rendered height before tab switches.
	const miniModalShellRef = React.useRef<HTMLDivElement | null>(null);

	const { hasTabsView, getDynamicContainerStyle, setTabShellHeightPx, setForYouShellHeightPx } =
		useMiniModalTabHeight({
			isEmptyConvo,
			activeAction,
			screenType,
			isAutoSuggestSkillsVisible,
			topOffset,
		});

	// Routes the captured shell height to the correct setter based on which tab we're leaving.
	// Suggestions height → tabShellHeightPx, For You height → forYouShellHeightPx.
	const handleShellHeightCapture = React.useCallback(
		(fromTabId: string, heightPx: number) => {
			if (fromTabId === FOR_YOU_TAB_ID) {
				setForYouShellHeightPx(heightPx);
			} else {
				setTabShellHeightPx(heightPx);
			}
		},
		[setTabShellHeightPx, setForYouShellHeightPx],
	);

	const safeMaxHeight = `min(${MINI_MODAL_EXPANDED_HEIGHT}, calc(100vh - ${topOffset}px))`;

	const onInsightReady = useSeedInsightConversation();

	const renderMiniModalMainView = ({
		wrapperRef,
		ResizeHandlers,
		wrapperStyle,
		isResizeEnabled,
		isResizeHeaderThresholdReached,
	}: UseResizeReturn) => {
		const dynamicContainerStyle = getDynamicContainerStyle(wrapperStyle);
		miniModalShellRef.current = wrapperRef.current;

		const mainContent = (
			<ChatInputPlaceholderOverrideProvider>
				<Box
					testId="conversation-assistant-mini-modal-main-container"
					xcss={styles.miniModalMainContainer}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-dynamic resize dimensions
					style={dynamicContainerStyle}
					ref={wrapperRef}
				>
					{navigationRefreshOrNull}
					<Box
						testId="conversation-assistant-mini-modal-header-container"
						xcss={styles.miniModalHeaderContainer}
					>
						<ActiveTabAwareChatHeader {...chatHeaderProps} />
					</Box>

					{/* Space bar: same render contract as the main view (see renderMainView()).
					    Mirrored here so the space bar appears in mini-modal mode as well. */}
					<SelectedSpaceBar cloudId={config.siteId} emojiProvider={spaceEmojiProvider} />

					{/* this component is only rendered if ChatHistory and ChatInput don't contain any data */}
					{isFullScreenErrorState && (
						<ChatGenericError
							errorType={getChatGenericErrorType(conversationError!.underlyingError)}
						/>
					)}

					{!isFullScreenErrorState && (
						<>
							{isChatOnboardingMiniModalOpen ? (
								<ChatOnboarding product={config.product} />
							) : (
								<Box
									testId="conversation-assistant-mini-modal-body-container"
									xcss={styles.miniModalBodyContainer}
								>
									{smartLinkPostAuthProps ? (
										<Suspense
											fallback={null}
											interactionName="smartlink-3p-post-auth-mini-modal-body"
										>
											<LazySmartLink3PPostAuthActions {...smartLinkPostAuthProps} />
										</Suspense>
									) : (
										<Box xcss={styles.chatWithOverlayWrapper}>
											<ActiveTabAwareChatHistory
												conversationId={currentConversationId}
												mode={mode}
												handleClickDynamicPrompt={handleClickStarter}
												screenType={screenType}
												closeInlineBrowse={closeInlineBrowse}
												isResizeEnabled={isResizeEnabled}
												isResizeHeaderThresholdReached={isResizeHeaderThresholdReached}
												onDiscoverPromptLibraryClick={handleDiscoverPromptLibraryClick}
												product={config.product}
												onOpenMemoryManagement={onSettingsClick}
											/>

											{enableAutoSuggest && (
												<AutoSuggestSkills
													onDismissHandle={onAutoSuggestSkillsDismissHandle}
													searchQuery={autoSuggestSearchQuery}
													siteId={config.siteId}
													product={config.product}
													experienceId={config.experienceId ?? AI_MATE_EXPERIENCE_ID}
													emojiProvider={spaceEmojiProvider}
													onViewAllSkills={handleViewAllSkillsFromAutoSuggest}
													onVisibleChange={setIsAutoSuggestSkillsVisible}
												/>
											)}
										</Box>
									)}
								</Box>
							)}

							<Box
								testId="conversation-assistant-mini-modal-footer-container"
								xcss={styles.miniModalFooterContainer}
							>
								<MiniModalTabAwareChatInput hasTabsView={hasTabsView}>
									<ErrorBoundary
										onError={handleUnhandledError}
										fallbackRender={() => <ChatGenericError errorType="generic" />}
									>
										{conversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ? (
											<DeactivatedAgentError
												handleNewChatClick={handleNewChatClick}
												errorCode={conversation?.errorCode}
											/>
										) : (
											showInput && (
												<ChatInputOverlay>
													{(overrideProps) => (
														<ChatInputRefresh
															{...overrideProps}
															initialPromptText={initialPromptText}
															shouldSeedInitialPromptText={shouldSeedInitialPromptText}
															suppressInitialFocus={suppressInitialFocus}
															mode={mode}
															editorPopupsMountPoint={editorPopupsMountPoint || undefined}
															shouldRenderQuickActionsInPortal={
																mode === ConversationAssistantUIModes.MINI_MODAL
															}
															isDisabled={
																isRateLimitedState && rateLimitedActionType !== 'deep-research'
															}
															conversationId={currentConversationId}
															enableVoiceDictation={elements?.voiceDictationButton}
															dictationOverlayBackground={getDictationOverlayBackground(
																'mini-chat',
															)}
															isExpanded={isPromptLibraryExpanded}
															activeAction={activeAction}
															onDiscoverSkillsClick={handleDiscoverSkillsClick}
															onBrowsePromptLibraryClick={handleBrowsePromptLibraryFromMenuClick}
															onCreateSpaceClick={handleCreateSpaceClick}
															onAutoSuggestQueryChange={handleAutoSuggestQueryChange}
															enableSkills={enableSkills}
															enableAgentMentions={enableAgentMentions}
															showActionsButton={showActionsButton}
														/>
													)}
												</ChatInputOverlay>
											)
										)}
									</ErrorBoundary>
								</MiniModalTabAwareChatInput>
								<UFOSegment name={'main-footer'}>
									<MainFooter isRefreshVersion={true} />
								</UFOSegment>
							</Box>
						</>
					)}
					<ResizeHandlers />
				</Box>
			</ChatInputPlaceholderOverrideProvider>
		);

		const activeMenuContent = isPulseExperimentEnabled ? (
			<Box
				testId="conversation-assistant-mini-modal-main-active-panel-container"
				xcss={styles.miniModalMainActivePanelContainer}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-dynamic resize dimensions
				style={dynamicContainerStyle}
				ref={wrapperRef}
			>
				{renderNavAndActivePanelMenu()}
				<ResizeHandlers />
			</Box>
		) : (
			<Box
				testId="conversation-assistant-mini-modal-main-active-panel-container"
				xcss={styles.miniModalMainActivePanelContainer}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-dynamic resize dimensions
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-dynamic resize dimensions
					maxHeight: safeMaxHeight,
				}}
			>
				{renderNavAndActivePanelMenu()}
			</Box>
		);

		return activeMenu === 'none' ? mainContent : activeMenuContent;
	};

	const renderMainView = (
		options: {
			elements?: Partial<NonNullable<UIConfig['elements']>>;
		} = {},
	) => {
		const resolvedElements = {
			...elements,
			...options.elements,
		};
		const isSidebarWithTabs =
			mode === ConversationAssistantUIModes.SIDEBAR &&
			!fullScreen &&
			FeatureGates.getExperimentValue<boolean>('rovo_chat_spaces_experiment', 'isEnabled', false);

		const mainContent = (
			<ChatInputPlaceholderOverrideProvider>
				<Flex direction="column" xcss={styles.mainContent}>
					{navigationRefreshOrNull}
					{resolvedElements?.mainChatHeader && (
						<UFOSegment name={'chat-header'}>
							{isSidebarWithTabs ? (
								<ActiveTabAwareChatHeader
									{...chatHeaderProps}
									onMenuButtonClick={
										!resolvedElements?.stagingAreaEnabled ? onChatHeaderMenuButtonClick : undefined
									}
								/>
							) : (
								<ChatMainHeaderRefresh
									{...chatHeaderProps}
									onMenuButtonClick={
										!resolvedElements?.stagingAreaEnabled ? onChatHeaderMenuButtonClick : undefined
									}
								/>
							)}
						</UFOSegment>
					)}

					{/* Space bar: shown when the active chat is associated with a Rovo Space.
					    Reads `selectedSpace` from the ui-store and is gated behind
					    `rovo_chat_spaces_experiment` internally — see `./ui/selected-space-bar`. */}
					<SelectedSpaceBar cloudId={config.siteId} emojiProvider={spaceEmojiProvider} />

					{/* this component is only rendered if ChatHistory and ChatInput don't contain any data */}
					{isFullScreenErrorState && (
						<ChatGenericError
							errorType={getChatGenericErrorType(conversationError!.underlyingError)}
						/>
					)}
					{!isFullScreenErrorState &&
						(() => {
							const chatInput = (
								<UFOSegment name={'chat-input-outer'}>
									<ErrorBoundary
										onError={handleUnhandledError}
										fallbackRender={() => <ChatGenericError errorType="generic" />}
									>
										{conversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ? (
											<DeactivatedAgentError
												handleNewChatClick={handleNewChatClick}
												errorCode={conversation?.errorCode}
											/>
										) : (
											showInput && (
												<>
													<ChatInputPlacementContainer
														shouldShow={chatStatus !== 'launching' && !isAifcOnboardingConfluence}
														fullScreen={fullScreen}
													/>
													<ChatInputOverlay>
														{(overrideProps) => (
															<ChatInputRefresh
																{...overrideProps}
																initialPromptText={initialPromptText}
																shouldSeedInitialPromptText={shouldSeedInitialPromptText}
																suppressInitialFocus={suppressInitialFocus}
																// Report the correct `mode` to dictation telemetry in fullscreen
																// (was incorrectly 'sidebar'). Telemetry-only, no behavior change.
																// Killswitch (default ON) as a safety net for analytics consumers.
																mode={
																	fullScreen && fg('ai_speech_to_text_fullscreen_mode_killswitch')
																		? ConversationAssistantUIModes.FULL_SCREEN_MODAL
																		: mode
																}
																isDisabled={
																	isRateLimitedState && rateLimitedActionType !== 'deep-research'
																}
																conversationId={currentConversationId}
																enableVoiceDictation={elements?.voiceDictationButton}
																isExpanded={isPromptLibraryExpanded}
																activeAction={activeAction}
																onDiscoverSkillsClick={handleDiscoverSkillsClick}
																onBrowsePromptLibraryClick={handleBrowsePromptLibraryFromMenuClick}
																onCreateSpaceClick={handleCreateSpaceClick}
																editorPopupsMountPoint={editorPopupsMountPoint || undefined}
																onAutoSuggestQueryChange={handleAutoSuggestQueryChange}
																enableSkills={enableSkills}
																enableAgentMentions={enableAgentMentions}
																showActionsButton={showActionsButton}
															/>
														)}
													</ChatInputOverlay>
												</>
											)
										)}
									</ErrorBoundary>
								</UFOSegment>
							);

							return (
								<>
									<div
										css={[
											fullScreen && isEmptyConvo && !shouldSuppressPromptLibrary
												? styles.contentWrapperCentered
												: styles.contentWrapper,
											fullScreen &&
												isEmptyConvo &&
												!shouldSuppressPromptLibrary &&
												isPromptLibraryExpanded &&
												styles.contentWrapperCenteredPromptLibraryExpanded,
											!fullScreen && styles.chatWithOverlayWrapper,
										]}
									>
										{isSidebarWithTabs ? (
											<UFOSegment name={'chat-history'}>
												<>
													{/* for UFO performance tracking dimensions */}
													<UFOCustomData data={{ activeMenu: 'conversation-history' }} />
													<ActiveTabAwareChatHistory
														conversationId={currentConversationId}
														mode={mode}
														handleClickDynamicPrompt={handleClickStarter}
														screenType={screenType}
														closeInlineBrowse={closeInlineBrowse}
														onDiscoverPromptLibraryClick={handleDiscoverPromptLibraryClick}
														product={config.product}
														onOpenMemoryManagement={onSettingsClick}
													/>
												</>
											</UFOSegment>
										) : (
											<UFOSegment name={'chat-history'}>
												<>
													{/* for UFO performance tracking dimensions */}
													<UFOCustomData data={{ activeMenu: 'conversation-history' }} />
													<ChatHistory
														conversationId={currentConversationId}
														mode={mode}
														handleClickDynamicPrompt={handleClickStarter}
														screenType={screenType}
														closeInlineBrowse={closeInlineBrowse}
														onDiscoverPromptLibraryClick={handleDiscoverPromptLibraryClick}
														product={config.product}
														onOpenMemoryManagement={onSettingsClick}
													/>
												</>
											</UFOSegment>
										)}
										{!fullScreen && stagingAreaMode !== 'inline' && enableAutoSuggest && (
											<AutoSuggestSkills
												onDismissHandle={onAutoSuggestSkillsDismissHandle}
												searchQuery={autoSuggestSearchQuery}
												siteId={config.siteId}
												product={config.product}
												experienceId={config.experienceId ?? AI_MATE_EXPERIENCE_ID}
												emojiProvider={spaceEmojiProvider}
												onViewAllSkills={handleViewAllSkillsFromAutoSuggest}
												onVisibleChange={setIsAutoSuggestSkillsVisible}
											/>
										)}
										{fullScreen && (
											<div
												css={[
													styles.fullscreenPromptContainer,
													stagingAreaRef && styles.fullscreenPromptContainerWithStagingAreaRef,
												]}
											>
												{chatInput}
												{shouldRenderPromptLibrary && (
													<div css={styles.fullscreenPromptArea}>
														<Suspense
															fallback={<></>}
															interactionName="skill-metadata-prompt-library"
														>
															<SkillMetadataProvider
																siteId={config.siteId}
																tags={{ skillRegistryTags: [PROMPT_LIBRARY_SKILL_TAG] }}
															>
																<LazyPromptLibrary
																	isExpanded={isPromptLibraryExpanded}
																	onExpandedChange={handlePromptLibraryExpandedChange}
																	conversationId={currentConversationId}
																/>
															</SkillMetadataProvider>
														</Suspense>
														{stagingAreaMode !== 'inline' && enableAutoSuggest && (
															<AutoSuggestSkills
																layout="fullscreen"
																onDismissHandle={onAutoSuggestSkillsDismissHandle}
																searchQuery={autoSuggestSearchQuery}
																siteId={config.siteId}
																product={config.product}
																experienceId={config.experienceId ?? AI_MATE_EXPERIENCE_ID}
																emojiProvider={spaceEmojiProvider}
																onViewAllSkills={handleViewAllSkillsFromAutoSuggest}
																onVisibleChange={setIsAutoSuggestSkillsVisible}
															/>
														)}
													</div>
												)}
											</div>
										)}
									</div>
									{!fullScreen && (
										<div css={styles.contentFooter}>
											<MiniModalTabAwareChatInput hasTabsView={isSidebarWithTabs}>
												{chatInput}
											</MiniModalTabAwareChatInput>
										</div>
									)}
								</>
							);
						})()}
					<UFOSegment name={'main-footer'}>
						<MainFooter isRefreshVersion={true} />
					</UFOSegment>
				</Flex>
			</ChatInputPlaceholderOverrideProvider>
		);

		// TEMP FIX since we can't rely on config for single source of truth for full screen experience
		// TODO to cleanup this implementation once we can accurately rely on the config as a single source of truth for full screen experience
		return activeMenu === 'none' ? mainContent : renderNavAndActivePanelMenu();
	};

	const isMinimodalExperience = mode === ConversationAssistantUIModes.MINI_MODAL;
	const isFullScreenModalExperience =
		mode === ConversationAssistantUIModes.FULL_SCREEN_MODAL &&
		isFullScreenModalAvailable &&
		fg('agent_session_experience_fg');

	// TEMP FIX since we can't rely on config for single source of truth for full screen experience
	// TODO to cleanup this implementation once we can accurately rely on the config as a single source of truth for full screen experience
	const isFullScreenExperience = !!fullScreen;

	const pageTitle = useSelectedAgentAsPageTitle(
		selectedAgent?.name,
		selectedAgent?.is_default,
		currentConversationId,
		conversation?.name,
	);

	const greetingHeight = elements?.shouldShowGreeting ? MINI_MODAL_GREETING_HEIGHT : 0;
	const greetingKey = customGreeting ? 'custom' : 'default';

	return (
		<>
			<ShareConversationModalContainer
				state={shareConversationModalState}
				cloudId={config.siteId}
				principalId={config.userId}
			/>
			<FeedbackCollectorContainerWrapper>
				<ShareUpToMessageProviderWrapper
					shareUpToMessage={shareUpToMessage}
					isMessageShareable={shareConversationModalState.isMessageShareable}
					shareDisabledReason={shareConversationModalState.shareDisabledReason}
				>
					<ModalContextProvider>
						<ProfileTriggerProvider renderProfileTrigger={renderProfileTrigger}>
							<BackBehaviorClearWhenConversationChanges />
							{isPulseExperimentEnabled && <InsightsChatExitedPublisher />}
							<RovoSkillsConfigProvider config={rovoSkillsConfig}>
								<ActionsProvider
									getActions={mergedGetActions}
									prioritizeCustomActionResults={
										replaceProviderActions || prioritizeCustomActionResults
									}
									numStartersToShow={customNumStartersToShow ?? numStartersToShow}
								>
									<MainWrapper testId={testId} isNav4Enabled={isNav4Enabled} mode={mode}>
										{/* for UFO performance tracking dimensions */}
										<UFOCustomData data={{ isEmptyConversation: isEmptyConvo }} />
										{/* Notes:
										 * 1. We only want to render the AutoConversationEvaluator in full screen mode because the experiment is only targeting this surface area
										 * 2. We only want to render if the conversation is empty. This is just an extra safeguard and our trait logic should catch this anyway since it checks if a user has ever sent a rovo chat message before / convo will be empty for these users
										 * 3. We shouldn't render until config.userId is available - the userId takes a bit to mount & hydrate
										 * 4. Despite the name, the "killswitch" gate acts as a launch gate (off = suppressed,
										 *    on = enabled), not a killswitch. Will clean up post-experiment.
										 */}
										{!!fullScreen &&
											isEmptyConvo &&
											config.userId &&
											fg('rovo-chat-auto-conversation-evaluator-killswitch') && (
												<AutoConversationEvaluator
													userId={config.userId}
													firedRef={autoConversationFiredRef}
												/>
											)}
										{isFullScreenExperience ? (
											<>
												{renderMainView()}
												{!hideBrowserTabTitle && <PageTitle title={pageTitle} />}
												{!hideBrowserTabFavicon &&
													fg('rovo_chat_selected_agent_favicon_enabled') && (
														<AgentFavicon
															selectedAgent={selectedAgent}
															currentConversationId={currentConversationId}
														/>
													)}
											</>
										) : isFullScreenModalExperience ? (
											<ActiveTabProvider
												currentConversationId={currentConversationId}
												selectedAgentId={selectedAgentId}
												miniModalShellRef={miniModalShellRef}
												captureShellHeightOnTabChange={hasTabsView}
												onShellHeightCapture={handleShellHeightCapture}
											>
												<ForYouNavigationProvider>
													<FullScreenModal
														isOpen={true}
														onOpenChange={(isOpen) => {
															if (!isOpen) {
																closeChat();
															}
														}}
														chatHeaderProps={chatHeaderProps}
														modalContent={renderMainView({
															elements: { mainChatHeader: false },
														})}
													/>
												</ForYouNavigationProvider>
											</ActiveTabProvider>
										) : (
											<ActiveTabProvider
												currentConversationId={currentConversationId}
												selectedAgentId={selectedAgentId}
												miniModalShellRef={miniModalShellRef}
												captureShellHeightOnTabChange={hasTabsView}
												onShellHeightCapture={handleShellHeightCapture}
											>
												<ForYouNavigationProvider>
													{isMinimodalExperience ? (
														<MiniModalViewWrapper
															renderMiniModalMainView={renderMiniModalMainView}
															greetingHeight={greetingHeight}
															greetingKey={greetingKey}
															topOffset={topOffset}
															enableMiniModalResize={enableMiniModalResize}
														/>
													) : (
														<Flex xcss={styles.navBarWrapper}>{renderMainView()}</Flex>
													)}
												</ForYouNavigationProvider>
											</ActiveTabProvider>
										)}
										<SBSIntegrationWrapper
											userId={config.userId ?? ''}
											siteId={config.siteId}
											product={config.product}
											conversationId={currentConversationId}
											conversationStatus={conversation?.status}
										/>
										<DebugModal />
										{fg('rovo_agents_chat_debug_mode_enabled') && <AgentDebugModal />}
										<PubSubListener />
										<ForgeAuthListener />
										{fg('build_rovo_agent_sticky_skill') && (
											<StickySkillListener
												key={currentConversationId}
												conversationId={currentConversationId}
											/>
										)}
										<FeedbackCollector />
										{/* Note: The introduction of Chat Framework Refresh (rovo_chat_framework_refresh) means the agents experience loads
			within the panel now, however this modal must remain for Atlassian home to trigger open via Rovo Triggers URL params */}
										<AgentModal />
										<DataSculptorModal />
										<AnalyticsPropertiesUpdater mode={mode} />
										<StreamingStatusDispatcher />
										<SelectedAgentPublisher />
										<ExperienceSuccess name={ExperienceName.LOAD_PANEL} />
										<div
											ref={setEditorPopupsMountPoint}
											data-testid="rovo-chat-input-popups-mount-point"
										/>
										{!elements?.stagingAreaEnabled && fg('aifc_create_enabled') && (
											<Suspense
												interactionName="conversation-assistant-smart-creation-suspense"
												fallback={<></>}
											>
												<LazySmartCreationWrapper
													config={config}
													handleSubmit={customHandlers?.handleSubmitSmartCreate ?? (() => {})}
													handleClose={() => {
														customHandlers?.handleCloseSmartCreate?.({ currentConversationId });
														resetStagingArea();
													}}
													shouldShowBackButton={false}
													useResendMessage={useResendMessage}
													enableVoiceDictation={elements?.voiceDictationButton}
												/>
											</Suspense>
										)}
										{fg('fe_jira_admin_agent_eap') && <RovodalHost config={config} />}
									</MainWrapper>
								</ActionsProvider>
							</RovoSkillsConfigProvider>
							{FeatureGates.getExperimentValue(
								'rovo_chat_spaces_experiment',
								'isEnabled',
								false,
							) && (
								<SpaceModalsWithStore
									cloudId={config.siteId}
									isCreateSpaceModalOpen={isCreateSpaceModalOpen}
									onCloseCreateSpace={() => setIsCreateSpaceModalOpen(false)}
									recommendedSpaceModal={recommendedSpaceModal}
									onCloseRecommendedSpace={() => setRecommendedSpaceModal({ isOpen: false })}
									sideNavSpaceCreatedRef={onSideNavSpaceCreatedRef}
									isManageSpaceModalOpen={isManageSpaceModalOpen}
									onCloseManageSpace={() => setIsManageSpaceModalOpen(false)}
									selectedSpace={selectedSpace}
									activeMenu={activeMenu}
									assistanceServiceParams={{
										touchpointSource: 'rovo-chat',
										product: config.product,
										experienceId: config.experienceId ?? AI_MATE_EXPERIENCE_ID,
										siteId: config.siteId,
									}}
								/>
							)}
						</ProfileTriggerProvider>
					</ModalContextProvider>
				</ShareUpToMessageProviderWrapper>
			</FeedbackCollectorContainerWrapper>
		</>
	);
};

type ConversationAssistantRenderProps = React.ComponentProps<
	typeof ConversationAssistantRenderComponent
>;

// rovo_ttai_segment_render_stabilization: absorb parent render waves when the root
// segment's props are stable.
const MemoizedConversationAssistantRender = React.memo(
	ConversationAssistantRenderComponent,
	shallowEqualObjects,
);

// rovo_ttai_segment_render_stabilization ON: subscribe to only the four UI-store
// fields the root segment reads via the narrow composite selector, so the root
// re-renders only when one of those fields changes rather than on every UI-store
// state change.
const ConversationAssistantRenderWithSelector = (
	props: Omit<
		ConversationAssistantRenderProps,
		'activeMenu' | 'isChatOnboardingMiniModalOpen' | 'selectedSpace' | 'smartLink3PPostAuth'
	>,
): JSX.Element => {
	di(useRootUISelector);
	const [{ activeMenu, isChatOnboardingMiniModalOpen, selectedSpace, smartLink3PPostAuth }] =
		useRootUISelector();
	return (
		<MemoizedConversationAssistantRender
			{...props}
			activeMenu={activeMenu}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			selectedSpace={selectedSpace}
			smartLink3PPostAuth={smartLink3PPostAuth}
		/>
	);
};

// rovo_ttai_segment_render_stabilization OFF/control: subscribe to the broad UI
// store, matching the prior behaviour where the root re-rendered on any UI-store
// state change.
const ConversationAssistantRenderWithBroadStore = (
	props: Omit<
		ConversationAssistantRenderProps,
		'activeMenu' | 'isChatOnboardingMiniModalOpen' | 'selectedSpace' | 'smartLink3PPostAuth'
	>,
): JSX.Element => {
	di(useUIStore);
	const { activeMenu, isChatOnboardingMiniModalOpen, selectedSpace, smartLink3PPostAuth } =
		useUIStore();
	return (
		<ConversationAssistantRenderComponent
			{...props}
			activeMenu={activeMenu}
			isChatOnboardingMiniModalOpen={isChatOnboardingMiniModalOpen}
			selectedSpace={selectedSpace}
			smartLink3PPostAuth={smartLink3PPostAuth}
		/>
	);
};

export const ConversationAssistantRender = (
	props: Omit<
		ConversationAssistantRenderProps,
		'activeMenu' | 'isChatOnboardingMiniModalOpen' | 'selectedSpace' | 'smartLink3PPostAuth'
	>,
): JSX.Element => {
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<ConversationAssistantRenderWithSelector {...props} />
	) : (
		<ConversationAssistantRenderWithBroadStore {...props} />
	);
};
